import { Popover, PopoverButton } from '@headlessui/react';
import { usePathname } from 'next/navigation';
import { lazy, useCallback, useEffect, useRef, useState } from 'react';
import React from 'react';

import DownChevronIcon from '@/icons/downChevron';

import { ISubMenuPopoverContentProps } from './SubMenuPopoverContent';

const preloadedSubMenuPopoverContent = import('./SubMenuPopoverContent');
const SubMenuPopoverContent = lazy(() => preloadedSubMenuPopoverContent);

const useOutsideClick = (ref: React.RefObject<HTMLElement>, callback: () => void, isActive: boolean) => {
	useEffect(() => {
		if (!isActive) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (
				ref.current &&
				!ref.current.contains(event.target as Node) &&
				!(event.target as Element).closest('[data-headlessui-state="open"]')
			) {
				callback();
			}
		};

		document.addEventListener('mousedown', handleClickOutside, { capture: true });
		return () => document.removeEventListener('mousedown', handleClickOutside, { capture: true });
	}, [ref, callback, isActive]);
};

interface ISubMenuPopoverProps extends ISubMenuPopoverContentProps {
	title: string;
	className?: string;
}

const SubMenuPopover = ({ className = '', title, ...props }: ISubMenuPopoverProps) => {
	const subMenuRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [closeFunc, setCloseFunc] = useState<(() => void) | null>(null);
	const pathName = usePathname();

	const checkTitle = `/${title.toLowerCase()}`;
	const isActiveLink =
		pathName.includes(checkTitle) || (checkTitle === '/data' && (pathName === '/' || pathName === '/details'));

	useOutsideClick(
		subMenuRef as React.RefObject<HTMLElement>,
		useCallback(() => closeFunc?.(), [closeFunc]),
		isOpen
	);

	return (
		<Popover className={`relative ${className}`}>
			{({ open, close }) => {
				setIsOpen(open);
				if (open) setCloseFunc(() => close);
				return (
					<div
						ref={subMenuRef}
						onClick={() => !open && close()}
						className={`group relative flex h-full cursor-pointer items-center px-3 py-2 text-base font-medium after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-[linear-gradient(162deg,#DD1438_0%,#5B01A2_100%)] after:opacity-0 hover:after:opacity-100 ${isActiveLink ? 'after:opacity-100' : ''}`}>
						<PopoverButton
							className={`flex h-full items-center space-x-2 rounded-md focus:outline-none group-hover:text-midnight dark:group-hover:text-white ${isActiveLink ? 'text-midnight dark:text-white' : 'text-gray dark:text-header-gray'}`}>
							<span className="text-sm font-medium uppercase tracking-[0.125rem]">{title}</span>
							<DownChevronIcon
								className={`flex items-center justify-center text-inherit transition-all duration-300 ${open ? 'rotate-180' : ''}`}
							/>
						</PopoverButton>
						<SubMenuPopoverContent close={close} {...props} />
					</div>
				);
			}}
		</Popover>
	);
};

export default React.memo(SubMenuPopover);
